import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, IconButton, Tooltip, useTheme } from '@mui/material';
import { IconMessageCircle } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../hooks/useAuth';
import { getChatUnreadCount } from '../../api/Chat/chatService';
import {
  CHAT_CONNECTION_STATE,
  CHAT_EVENTS,
  acquireChatConnection,
  onChatConnectionStateChange,
  onChatEvent,
} from '../../services/chatSignalR';

const REFRESH_DEBOUNCE_MS = 400;
const OFFLINE_POLL_MS = 60000;

const hasChatRoute = (routes = []) =>
  routes.some(route =>
    (route.routePath === ROUTES.CHAT && route.canView) ||
    route.children?.some(child => child.routePath === ROUTES.CHAT && child.canView)
  );

const ChatNavButtonInner = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const timerRef = useRef(null);
  const connectedRef = useRef(false);

  const refresh = useCallback(async () => {
    const response = await getChatUnreadCount();
    if (response?.success) setUnreadCount(response.data?.unreadCount || 0);
  }, []);

  useEffect(() => {
    let active = true;
    const scheduleRefresh = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => active && refresh(), REFRESH_DEBOUNCE_MS);
    };

    const release = acquireChatConnection();
    const offMessage = onChatEvent(CHAT_EVENTS.RECEIVE_MESSAGE, scheduleRefresh);
    const offRead = onChatEvent(CHAT_EVENTS.MESSAGES_READ, scheduleRefresh);
    const offState = onChatConnectionStateChange(state => {
      connectedRef.current = state === CHAT_CONNECTION_STATE.CONNECTED;
      // A failed connect is often an expired token: this REST call lets baseFetch
      // refresh it, and the next connection attempt picks the new token up.
      if (state === CHAT_CONNECTION_STATE.CONNECTED || state === CHAT_CONNECTION_STATE.DISCONNECTED) {
        scheduleRefresh();
      }
    });

    // Without a live connection there are no events, so fall back to slow polling.
    const poll = setInterval(() => {
      if (!connectedRef.current) refresh();
    }, OFFLINE_POLL_MS);

    refresh();

    return () => {
      active = false;
      clearTimeout(timerRef.current);
      clearInterval(poll);
      offMessage();
      offRead();
      offState();
      release();
    };
  }, [refresh]);

  return (
    <Tooltip title="Chat">
      <IconButton
        className="head-icon-btn"
        aria-label={unreadCount ? `Chat, ${unreadCount} unread messages` : 'Chat'}
        onClick={() => location.pathname !== ROUTES.CHAT && navigate(ROUTES.CHAT)}
        sx={{
          color: theme.palette.background.text,
          borderColor: theme.palette.custom?.borderColor,
        }}
      >
        <Badge color="primary" badgeContent={unreadCount} max={99}>
          <IconMessageCircle stroke={2} size={16} />
        </Badge>
      </IconButton>
    </Tooltip>
  );
};

/** Header shortcut to chat with the total unread count. Hidden when the user's role has no chat menu. */
const ChatNavButton = () => {
  const { user } = useAuth();
  if (!hasChatRoute(user?.routes)) return null;
  return <ChatNavButtonInner />;
};

export default ChatNavButton;
